import React, {useEffect, useState} from "react";
import {Box, Button, Card, Chip, Icon, Select, TextField} from "@nike/eds";
import useSessionStorage from "../../hooks/useSessionStorage";
import {CaretLeft, CaretRight} from "@nike/nike-design-system-icons";
import {FlowSearchModel} from "../../model/FlowSearchModel";
import { useSearchParams } from "react-router-dom";
import useFieldOptions from "../../hooks/useFieldOptions";
import {edsSpace16} from "@nike/eds/dist/tokens";

const typesOptions = [
    {value: "Inbound Shipment", label: "Inbound Shipment"},
    {value: "Stock Transfer Shipment", label: "Stock Transfer Shipment"},
    {value: "Warehouse Entry", label: "Warehouse Entry"},
    {value: "Goods Receipt", label: "Goods Receipt"},
    {value: "Positive Adjustment", label: "Positive Adjustment"},
    {value: "Negative Adjustment", label: "Negative Adjustment"},
    {value: "Update Adjustment", label: "Update Adjustment"},
    {value: "Lock/Unlock", label: "Lock/Unlock"},
    {value: "Warehouse Removal", label: "Warehouse Removal"},
    {value: "Balancing", label: "Balancing"},
    {value: "Local Clearance Required", label: "Local Clearance Required"},
    {value: "T&T", label: "T&T Event"}
];

const statusOptions = [
    {value: "NEW", label: "New"},
    {value: "IGNORED", label: "Ignored"},
    {value: "PROCESSING", label: "Processing"},
    {value: "FAILED", label: "Failed"},
    {value: "PUBLISHED", label: "Published"},
    {value: "WAITING", label: "Waiting"},
    {value: "PROCESSED", label: "Processed"}
];

const findOptionByValue = (value, options) => {
    console.log('value', value);
    console.log('options', options);
    for (const e of options) {
        if (e.value === value) {
            return e;
        }
    }
    return null;
}

const transformToOptions = (options) => {
    if (options && options.map) {
        return options.map(e => {return {value: e, label: e}; });
    }
    return [];
};

const useSearchFieldOptions = () => {
    const shippingPoints = useFieldOptions("shippingPoint");
    const details = useFieldOptions("detail");
    const inventoryLocations = useFieldOptions("inventoryLocation");
    const shippingpointOptions = transformToOptions(shippingPoints);
    const detailOptions = transformToOptions(details);
    const inventoryLocationOptions = transformToOptions(inventoryLocations);
    return {
        shippingpointOptions,
        detailOptions,
        inventoryLocationOptions
    };
};

const emptySearchCriteria = () => {
    return {
        typeList: [],
        businessReference: '',
        statusList: [],
        startDate: '',
        endDate: '',
        flowId: '',
        detailList: [],
        shippingPointList: [],
        inventoryLocationList: []
    }
};

const emptyValidation = () => {
    return {
        hasErrors: false,
        startDate: '',
        endDate: ''
    };
};

const useSearchCriteria = () => {
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ searchCriteriaState, setSearchCriteria ] = useSessionStorage("flowSearchCriteria", emptySearchCriteria());
    const [ restoreUsed, setRestoreUsed ] = useState(false);
    let searchCriteria = searchCriteriaState.value;

    const flowId = searchParams.get("flowId");
    if (flowId && flowId !== "") {
        searchCriteria  = {
            ...emptySearchCriteria(),
            flowId: flowId
        };
    }

    return {
        criteria: searchCriteria,
        restored: searchCriteriaState.restored,
        restoreUsed: restoreUsed,
        restore: () => {
            setRestoreUsed(true);
        },
        clear: () => {
            setSearchParams({flowId: ""});
            setSearchCriteria(emptySearchCriteria());
        },
        update: (newState) => {
            setSearchCriteria(newState);
        }
    };
}

const trim = (str) => {
    const trimmed = str && str.trim ? str.trim() : undefined;
    if (trimmed === "") {
        return undefined;
    } else {
        return trimmed;
    }
}

function FlowSearchForm({ onSearch }) {
    const searchFieldOptions = useSearchFieldOptions();
    const searchCriteriaState = useSearchCriteria();
    const searchCriteria = searchCriteriaState.criteria;
    const setSearchCriteria = searchCriteriaState.update;
    const [ validationError, setValidationError ] = useState(emptyValidation());
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        const errors = emptyValidation();
        if (searchCriteria.startDate !== "" && searchCriteria.endDate !== "" && (searchCriteria.startDate > searchCriteria.endDate)) {
            errors.startDate = "The start date should be before the end date";
            errors.endDate = "The end date should be after the start date";
            errors.hasErrors = true;
        }
        setValidationError(errors);
    }, [searchCriteria.startDate, searchCriteria.endDate, setValidationError]);


    const clearSearchCriteria = () => {
        searchCriteriaState.clear();
    }

    const toggleShowSearch = () => {
        setShowSearch(!showSearch);
    }

    const SearchButton = () => {
        return (
            <Button
                variant="ghost"
                beforeSlot={!showSearch ? <CaretLeft/> : <CaretRight/>}
                onClick={toggleShowSearch}
                size={"small"}
            >{!showSearch ? 'Filter' : ''}
            </Button>
        );
    }

    const typeChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            typeList: event !== null ? [...searchCriteria.typeList, event.value] : searchCriteria.typeList
        });

    }
    const businessReferenceChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            businessReference: event.target.value
        });
    }

    const statusChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            statusList: event !== null ? [...searchCriteria.statusList, event.value] : searchCriteria.statusList
        });
    }
    const startDateChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            startDate: event.target.value
        });
    }
    const endDateChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            endDate: event.target.value
        });
    }
    const detailChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            detailList: event != null ? [...searchCriteria.detailList, event.value] : searchCriteria.detailList
        });
    }
    const shippingPointChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            shippingPointList: event != null ? [...searchCriteria.shippingPointList, event.value] : searchCriteria.shippingPointList
        });
    }
    const inventoryLocationChangeHandler = (event) => {
        setSearchCriteria({
            ...searchCriteria,
            inventoryLocationList: event != null ? [...searchCriteria.inventoryLocationList, event.value] : searchCriteria.inventoryLocationList
        });
    }

    const removeType = (t: string) => {
        setSearchCriteria({
            ...searchCriteria,
            typeList: searchCriteria.typeList.filter(x => x !== t)
        });
    };
    const removeStatus = (s: string) => {
        setSearchCriteria({
            ...searchCriteria,
            statusList: searchCriteria.statusList.filter(x => x !== s)
        });
    };
    const removeDetail = (d: string) => {
        setSearchCriteria({
            ...searchCriteria,
            detailList: searchCriteria.detailList.filter(x => x !== d)
        });
    };
    const removeShippingPoint = (sp: string) => {
        setSearchCriteria({
            ...searchCriteria,
            shippingPointList: searchCriteria.shippingPointList.filter(x => x !== sp)
        });
    };
    const removeInventoryLocation = (il: string) => {
        setSearchCriteria({
            ...searchCriteria,
            inventoryLocationList: searchCriteria.inventoryLocationList.filter(x => x !== il)
        });
    };

    const doSearch = () => {
        if (validationError.hasErrors) {
            return;
        }

        let searchFlowData: FlowSearchModel = {
            typeList: searchCriteria.typeList,
            businessReference: trim(searchCriteria.businessReference),
            startDate: new Date(searchCriteria.startDate),
            endDate: new Date(searchCriteria.endDate),
            statusList: searchCriteria.statusList,
            flowId: trim(searchCriteria.flowId),
            detailList: searchCriteria.detailList,
            shippingPointList: searchCriteria.shippingPointList,
            inventoryLocationList: searchCriteria.inventoryLocationList
        }
        onSearch(searchFlowData);
    };

    const submitHandler = (event) => {
        event.preventDefault();
        doSearch();
    }

    //No dependency list added -- effect function itself checks if it needs to run or not - easier than adding all dependencies
    useEffect(() => {
        if (searchCriteriaState.restored) {
            if (searchCriteriaState.restoreUsed) {
                //Ignore, we already triggered this
            } else {
                console.log("Trigger search based on restore");
                searchCriteriaState.restore();
                doSearch();
            }
        }
    });


    const flowIdSet = searchCriteria.flowId !== undefined && searchCriteria.flowId !== '';

    const columnOneStyle = { width: 350 };

    return (
        <div>
        {!showSearch ? (
                <div className="eds-grid eds-grid--m-cols-6 eds-gap--8">
                    <div className="eds-grid--m-col-6 eds-grid--m-row-1 eds-grid--item-align-start eds-grid--item-justify-end eds-spacing--p-12"
                        style={{"display": "flex"}}>
                        <Card className="eds-elevation--4">
                            <SearchButton />
                        </Card>
                    </div>
                </div>
            ) :
                <div style={{backgroundColor: "white"}}>
                    <form onSubmit={submitHandler}>
                        <div className="eds-grid eds-grid--m-cols-4 eds-gap--8">
                            <Card className="eds-grid--m-col-1 eds-grid--m-rows-5 eds-grid--item-align-start eds-spacing--p-4">

                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <TextField onChange={businessReferenceChangeHandler}
                                               id="businessReferenceSearchField"
                                               type="text"
                                               hasErrors={false}
                                               label="Business Reference"
                                               value={searchCriteria.businessReference}
                                               style={columnOneStyle}
                                               disabled={flowIdSet}
                                    />
                                </Card>
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4" >
                                    <div style={columnOneStyle}>
                                        <Select onChange={typeChangeHandler}
                                                isClearable={true}
                                                id="typeSelect"
                                                options={typesOptions}
                                                label="Type"
                                                isDisabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={columnOneStyle}>
                                        <Select onChange={detailChangeHandler}
                                                isClearable={true}
                                                id="detailSearchField"
                                                label="Detail"
                                                options={searchFieldOptions.detailOptions}
                                                isDisabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                            </Card>
                            <Card
                                className="eds-grid--m-col-1 eds-grid--m-rows-5 eds-grid--item-align-start eds-spacing--p-4">
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={{width: 300}}>
                                        <Select onChange={shippingPointChangeHandler}
                                                isClearable={true}
                                                id="shippingPointSelect"
                                                label="Shipping Point"
                                                options={searchFieldOptions.shippingpointOptions}
                                                isDisabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={{width: 300}}>
                                        <Select onChange={inventoryLocationChangeHandler}
                                                isClearable={true}
                                                id="inventoryLocationSelect"
                                                label="Inventory Location"
                                                options={searchFieldOptions.inventoryLocationOptions}
                                                isDisabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={{width: 300}}>
                                        <Select onChange={statusChangeHandler}
                                                isClearable={true}
                                                id="statusSelect"
                                                label="Status"
                                                options={statusOptions}
                                                isDisabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                            </Card>
                            <Card
                                className="eds-grid--m-col-1 eds-grid--m-rows-1 eds-grid--item-align-start eds-spacing--p-4">
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={{width: 300}}>
                                        <TextField
                                            onChange={startDateChangeHandler}
                                            value={searchCriteria.startDate}
                                            id="id"
                                            type="datetime-local"
                                            hasErrors={validationError.startDate !== ''}
                                            errorMessage={validationError.startDate}
                                            label="Enter a start date"
                                            disabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                                <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                    <div style={{width: 300}}>
                                        <TextField
                                            onChange={endDateChangeHandler}
                                            value={searchCriteria.endDate}
                                            id="id"
                                            type="datetime-local"
                                            hasErrors={validationError.endDate !== ''}
                                            errorMessage={validationError.endDate}
                                            label="Enter an end date"
                                            disabled={flowIdSet}
                                        />
                                    </div>
                                </Card>
                                { flowIdSet
                                    ? <Card className="eds-grid--m-row-1 eds-spacing--p-4">
                                        <div style={{width: 300}}>
                                            <TextField id="flowIdSearchField"
                                                       type="text"
                                                       hasErrors={false}
                                                       label="Flow id"
                                                       value={searchCriteria.flowId}
                                                       style={{width: 300}}
                                                       disabled
                                            />
                                        </div>
                                    </Card>
                                    : undefined}
                            </Card>
                            <Card className="eds-grid--m-col-1 eds-grid--m-row-1 eds-grid--item-align-start eds-grid--item-justify-end eds-spacing--p-12">
                                <div className="eds-grid eds-grid--m-cols-6 eds-gap--8">
                                    <Card className="eds-grid--m-col-6 eds-grid--m-row-1 eds-grid--item-align-start eds-grid--item-justify-end">
                                        <SearchButton />
                                    </Card>
                                </div>

                                <Card className="eds-grid--m-row-1 eds-spacing--mt-48">
                                    <Button
                                        variant="primary"
                                        type={"submit"}
                                        size={"small"} disabled={flowIdSet} >Apply Filter</Button>
                                    <Button className="eds-spacing--ml-8"
                                        onClick={clearSearchCriteria}
                                        variant="secondary"
                                        size={"small"}>Clear All</Button>
                                </Card>




                            </Card>
                        </div>
                    </form>

                    <div>
                        <Box style={{ margin: edsSpace16, padding: edsSpace16 }}>
                            {searchCriteria.typeList.map(type => (
                                <Chip
                                    as="button"
                                    afterSlot={<Icon name="Close" onClick={() => removeType(type)} />}
                                    style={{ marginRight: edsSpace16, marginBottom: edsSpace16 }}
                                >
                                    Type: {type}
                                </Chip>
                            ))}
                            {searchCriteria.statusList.map(status => (
                                <Chip
                                    as="button"
                                    afterSlot={<Icon name="Close" onClick={() => removeStatus(status)}/>}
                                    style={{ marginRight: edsSpace16, marginBottom: edsSpace16 }}
                                >
                                    Status: {status}
                                </Chip>
                            ))}
                            {searchCriteria.detailList.map(detail => (
                                <Chip
                                    as="button"
                                    afterSlot={<Icon name="Close" onClick={() => removeDetail(detail)}/>}
                                    style={{ marginRight: edsSpace16, marginBottom: edsSpace16 }}
                                >
                                    Detail: {detail}
                                </Chip>
                            ))}
                            {searchCriteria.shippingPointList.map(shippingPoint => (
                                <Chip
                                    as="button"
                                    afterSlot={<Icon name="Close" onClick={() => removeShippingPoint(shippingPoint)}/>}
                                    style={{ marginRight: edsSpace16, marginBottom: edsSpace16 }}
                                >
                                    Shipping Point: {shippingPoint}
                                </Chip>
                            ))}
                            {searchCriteria.inventoryLocationList.map(inventoryLocation => (
                                <Chip
                                    as="button"
                                    afterSlot={<Icon name="Close" onClick={() => removeInventoryLocation(inventoryLocation)}/>}
                                    style={{ marginRight: edsSpace16, marginBottom: edsSpace16 }}
                                >
                                    Inv. Location: {inventoryLocation}
                                </Chip>
                            ))}
                        </Box>
                    </div>
                </div>
        }
        </div>
    );
}

export {FlowSearchForm}
